import { compact } from 'fp-ts/lib/Array';
import {
  BoundSection,
  BoundView,
  BoundTab,
  BoundTenant,
  PathSlotted,
  CanBeDisabled,
  CanBeHidden,
} from 'configuration-schemas/bindings.types';
import { RouteLink } from 'src/types/RouteLink';
import { flatMap } from 'lodash/fp';
import { Option, fromNullable, chain, some, map, none, getOrElse, isSome } from 'fp-ts/lib/Option';
import { BoundViewZipper, BoundSectionZipper, BoundTabZipper } from 'src/pages/NavigationShell/NavigationZipper';
import { pipe } from 'fp-ts/lib/function';
import { option } from 'fp-ts';
import { isEmpty, isNil, find, isString, once } from 'lodash';
import { AppState } from 'src/store';
import { ContextMfpModule } from 'configuration-schemas/confdefn';
import { computeItemLink } from 'src/components/NavigationGroup/NavigationGroup';
import ServiceContainer from 'src/ServiceContainer';
import { logWarn } from 'src/services/loggingService';

export function lookupDefaultView(section: BoundSection): Option<BoundView> {
  return fromNullable(
    section.boundViews.find((view) => view.id === section.defaultViewId && !view.errorView && !view.hidden) ||
      section.boundViews.filter((view) => !view.hidden)[0],
  );
}

export function lookupDefaultSection(tab: BoundTab): Option<BoundSection> {
  return !isNil(tab.boundSections)
    ? fromNullable(tab.boundSections.find((section) => section.id === tab.defaultSectionId))
    : none; // disabled tabs will not have any bound sections
}

export function computeTabPath(tab: BoundTab): string {
  const tabSlot = tab.pathSlot;
  const section = lookupDefaultSection(tab);
  const view = pipe(section, chain(lookupDefaultView));
  const computedTabPath = compact([
    some(tabSlot),
    pipe(
      section,
      map((s) => s.pathSlot),
    ),
    pipe(
      view,
      map((v) => {
        // for workflow components append default workflow tab's pathSlot
        const defaultWorkflowTab = v.componentProps?.defaultPathSlot as string | undefined;
        return isNil(defaultWorkflowTab) || isEmpty(defaultWorkflowTab)
          ? v.pathSlot
          : `${v.pathSlot}/${defaultWorkflowTab}`;
      }),
    ),
  ]).join('/');
  return computedTabPath;
}

export function getPathSlot(s: PathSlotted): string {
  return s.pathSlot;
}

function getDefaultTab(perspectiveUi: BoundTenant): Option<BoundTab> {
  return fromNullable(perspectiveUi.defaultBoundTab);
}

export function computePerspectiveUiRouteSuffix(perspectiveUi: BoundTenant): string {
  const prefix = '/' + getPathSlot(perspectiveUi.perspective);
  const defaultTab = getDefaultTab(perspectiveUi);

  const rest = pipe(
    defaultTab,
    map(computeTabPath),
    map((x) => '/' + x),
    getOrElse(() => ''),
  );
  return prefix + rest;
}

export function computePerspectiveRoute(perspectiveUi: BoundTenant): string {
  return computePerspectiveUiRouteSuffix(perspectiveUi);
}

export function computeTabRouteLinks(defn: BoundTenant): RouteLink[] {
  return defn.boundTabs.map((tab) => ({
    name: tab.name,
    link: `/${getPathSlot(defn.perspective)}/${computeTabPath(tab)}`,
    highlightPrefix: `/${getPathSlot(defn.perspective)}/${tab.pathSlot}`,
    disabled: tab.disabled,
    hidden: tab.hidden,
    pathSlot: tab.pathSlot,
  }));
}

export function isNotHidden(v: CanBeHidden) {
  return !v.hidden;
}

export function computeViewRoutesForSection(defn: BoundTenant, tab: BoundTab, section: BoundSection): RouteLink[] {
  return section.boundViews.map((view) => ({
    name: view.name,
    disabled: view.disabled,
    hidden: view.hidden,
    link: computeItemLink(defn.perspective, tab, section, view),
    highlightPrefix:
      '/' + [getPathSlot(defn.perspective), getPathSlot(tab), getPathSlot(section), getPathSlot(view)].join('/'),
  }));
}

export function isNotDisabled(v: CanBeDisabled) {
  return !v.disabled;
}

export function computeViewRoutesForTab(defn: BoundTenant, tab: BoundTab): RouteLink[] {
  return flatMap(
    (section) => computeViewRoutesForSection(defn, tab, section),
    tab.boundSections?.filter(isNotDisabled),
  );
}

export function computeViewRouteLinks(defn: BoundTenant): RouteLink[] {
  return flatMap((tab) => computeViewRoutesForTab(defn, tab), defn.boundTabs.filter(isNotDisabled));
}

export type NavPath = [string, string, string, string, string?];

export const extractNavPathFromString = (path: string): Option<NavPath> => {
  // slice drops initial '' (or # when searching hashpath) produced by the leading slash
  const parts = path.split('/').slice(1);
  if (parts.length < 4) {
    // TargetSetting does 'custom' handling of window.location
    // which adds an extra element to the array when it comes here
    // Let elements beyond 4 pass through and parse, but drop them in the return below
    return none;
  }

  // At times the end slash may be present in the path but not needed (`some/path` vs `some/path/`)
  // causing the last element to be sent as an `""` instead of `undefined`
  // need to make sure that the last element remains undefined if it has no value
  const lastPathPart = parts[4];
  const formatted = isString(lastPathPart) && isEmpty(lastPathPart) ? undefined : lastPathPart;

  return some([parts[0], parts[1], parts[2], parts[3], formatted] as NavPath);
};
export function extractNavPath(location: Location, searchKey: 'pathname' | 'hash' = 'pathname'): Option<NavPath> {
  // hash key searching only used for initial state hydration
  return extractNavPathFromString(location[searchKey]);
}
export function getUrlWithoutFourthPath(path: string) {
  const parts = option.toUndefined(extractNavPathFromString(path));
  if (parts && parts.length == 5) {
    return '/' + [parts[0], parts[1], parts[2], parts[3]].join('/');
  }
  return path;
}

export function getFourthPathUrl(path: string, fourthPath: string): string {
  const paths = option.toUndefined(extractNavPathFromString(path));
  if (paths) {
    return '/' + paths.slice(0, 4).join('/') + '/' + fourthPath;
  }
  return path;
}

export function maybeAppendTrailingSlash(newUrl: string) {
  if (newUrl.substr(newUrl.length - 1) !== '/') {
    return newUrl + '/';
  }
  return newUrl;
}

export function getTabForPath(tenant: Pick<BoundTenant, 'boundTabs'>, navPath: NavPath): Option<BoundTab> {
  const pathSlot = navPath[1];
  return fromNullable(tenant.boundTabs.find((tab) => tab.pathSlot === pathSlot));
}

export function getSectionForPath(tab: Pick<BoundTab, 'boundSections'>, navPath: NavPath): Option<BoundSection> {
  const pathSlot = navPath[2];
  return fromNullable(tab.boundSections?.find((section) => section.pathSlot === pathSlot));
}

export function getViewForPath(tenant: BoundTenant, navPath: NavPath): Option<BoundView> {
  const pathSlot = navPath[3];
  const tab = getTabForPath(tenant, navPath);
  if (isSome(tab)) {
    const section = getSectionForPath(tab.value, navPath);
    if (isSome(section)) {
      return fromNullable(section.value.boundViews.find((v) => v.pathSlot === pathSlot));
    }
  }
  return none;
}

export function computeTabZipRoute(tabZip: Readonly<BoundTabZipper>): string {
  return '/' + [tabZip.getParent().getTenant().perspective.pathSlot, tabZip.getTab().pathSlot].join('/');
}

export function computeSectionZipRoute(sectionZip: Readonly<BoundSectionZipper>): string {
  return [computeTabZipRoute(sectionZip.getParent()), sectionZip.getSection().pathSlot].join('/');
}

export function computeViewZipRoute(viewZip: Readonly<BoundViewZipper>): string {
  return [computeSectionZipRoute(viewZip.getParent()), viewZip.getView().pathSlot].join('/');
}

// Log once per app load
const logMissingTabOnce = once((activeTab: string) => {
  logWarn(`[MFP CONFIG] Tab "${activeTab}" is not listed in the MFP context's tabs for this confdefn.`);
});

export const getMfpModule = (state: AppState): ContextMfpModule | undefined => {
  const activeTab = state.perspective.activeTab;

  // only consider logging inside a “full” MFP bubble
  const isFullMfpBubble = state.appConfig?.tenantConfig?.perspective?.headerbar?.type === 'MfpHeaderbar';

  // Find the first context object with mfpModule
  const contextWithMfpModule = find(state.appConfig?.tenantConfig?.context, (obj) => !!obj.mfpModule);

  // If no context object with mfpModule is found, return undefined
  if (!contextWithMfpModule) {
    return undefined;
  }

  // If the found context object contains tabs with activeTab, return its mfpModule
  if (contextWithMfpModule.tabs?.includes(activeTab)) {
    return contextWithMfpModule.mfpModule;
  }

  // Log only once, only in full MFP bubble if the above condition was not met
  if (isFullMfpBubble) {
    logMissingTabOnce(activeTab);
  }

  return undefined;
};

export const shouldActiveTabShowBookmarks = (state: AppState) => {
  const activeTab = state.perspective.activeTab;
  const showBookmarks = state.appConfig?.tenantConfig?.showBookmarks;
  const bookmarkContext = state.appConfig?.tenantConfig?.context?.bookmarks;
  let tabInBookmarkContext = false;
  if (bookmarkContext != null) {
    if (showBookmarks) {
      ServiceContainer.loggingService.warn(
        'showBookmarks=true supercedes context.bookmark. Remove `showBookmarks: true` if you want context.bookmark to restrict tabs.',
      );
    }
    tabInBookmarkContext = bookmarkContext.tabs.indexOf(activeTab) >= 0;
  }
  return tabInBookmarkContext || showBookmarks;
};
